import { useEffect } from 'react';

type SeoProps = {
  title: string;
  description: string;
  keywords?: string;
  path: string;
  image?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
};

const SITE_IMAGE =
  'https://readdy.ai/api/search-image?query=Split%20screen%20cinematic%20cover%20of%20a%20warm%20amber%20lit%20stage%20on%20the%20left%20and%20a%20cool%20teal%20dim%20backstage%20corridor%20on%20the%20right%2C%20strong%20visual%20contrast%2C%20moody%20editorial%20photography%2C%20film%20grain%2C%20minimal%20composition%2C%20no%20people%20no%20text&width=1200&height=630&seq=contrast-og-cover&orientation=landscape';

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export default function Seo({
  title,
  description,
  keywords,
  path,
  image,
  jsonLd,
}: SeoProps) {
  const jsonLdString = jsonLd ? JSON.stringify(jsonLd) : '';

  useEffect(() => {
    const basePath = typeof __BASE_PATH__ === 'string' ? __BASE_PATH__ : '/';
    const prefix = basePath.endsWith('/') ? basePath.slice(0, -1) : basePath;
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    const url = `${window.location.origin}${prefix}${normalizedPath}`;
    const ogImage = image || SITE_IMAGE;

    document.title = title;
    upsertMeta('name', 'description', description);
    if (keywords) upsertMeta('name', 'keywords', keywords);
    upsertMeta('name', 'robots', 'index,follow');

    upsertLink('canonical', url);

    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:image', ogImage);
    upsertMeta('property', 'og:site_name', '反差现场');

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', ogImage);

    const scriptId = 'page-jsonld';
    const existing = document.getElementById(scriptId);
    if (existing) existing.remove();
    if (jsonLdString) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      script.text = jsonLdString;
      document.head.appendChild(script);
    }

    return () => {
      const el = document.getElementById(scriptId);
      if (el) el.remove();
    };
  }, [title, description, keywords, path, image, jsonLdString]);

  return null;
}